import React, { useState, useEffect, useContext, useCallback } from 'react'; // Import useCallback
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, CircularProgress } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../contexts/AuthContext';
import Particles from 'react-tsparticles'; // Import Particles
import { loadSlim } from 'tsparticles-slim';  // Import slim version
import './InitializationPage.css';

const InitializationPage = () => {
    const { t } = useTranslation();
    const { isAuthenticated, isSessionLoading } = useContext(AuthContext);
    const navigate = useNavigate();
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [shuffledTips, setShuffledTips] = useState([]);

    // --- NEW: Particle background logic (from AuthPage) ---
    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    // Full particle configuration object
    const particlesConfig = {
        background: {
            color: {
                value: 'transparent',
            },
        },
        fpsLimit: 120,
        interactivity: {
            events: {
                onHover: {
                    enable: true,
                    mode: 'repulse',
                },
                resize: true,
            },
            modes: {
                repulse: {
                    distance: 100,
                    duration: 0.4,
                },
            },
        },
        particles: {
            color: {
                value: '#ffffff',
            },
            links: {
                color: '#4fc3f7',
                distance: 150,
                enable: true,
                opacity: 0.4,
                width: 1,
            },
            move: {
                direction: 'none',
                enable: true,
                outModes: {
                    default: 'bounce',
                },
                random: false,
                speed: 1,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    area: 800,
                },
                value: 80,
            },
            opacity: {
                value: 0.5,
            },
            shape: {
                type: 'circle',
            },
            size: {
                value: { min: 1, max: 3 },
            },
        },
        detectRetina: true,
    };

    useEffect(() => {
        // Get the tips from translation *inside* the effect
        const initialTips = t('initializationPage.loadingTips', { returnObjects: true });
        
        // Fisher-Yates shuffle algorithm
        const array = [...initialTips]; // Create a mutable copy
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]]; // Swap elements
        }
        setShuffledTips(array);
    }, [t]); // Use `t` as the dependency. It only changes when the language changes.

    useEffect(() => {
        if (!isSessionLoading) {
            const timer = setTimeout(() => {
                if (isAuthenticated) {
                    navigate('/app', { replace: true });
                } else {
                    navigate('/login', { replace: true });
                }
            }, 500); // Small delay for smoother transition
            return () => clearTimeout(timer);
        }
    }, [isSessionLoading, isAuthenticated, navigate]);

    // --- useEffect to cycle through the SHUFFLED tips ---
    useEffect(() => {
        // Don't start the timer until the shuffled tips are ready
        if (shuffledTips.length === 0) return;

        setCurrentTipIndex(0); // Reset index when tips change
        const timer = setInterval(() => {
            setCurrentTipIndex(prevIndex => (prevIndex + 1) % shuffledTips.length);
        }, 3000);
        
        return () => clearInterval(timer);
    }, [shuffledTips]); // Dependency is now the shuffled array

    return (
        <div className="init-page-wrapper">
            <Particles id="tsparticles-init" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('initializationPage.helmet.title')} - Vetra AI</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            
            {/* --- NEW: Animate the entire content box --- */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="init-content-box"
            >
                <img src="/static/vetraai_logo_grey.png" alt="Vetra AI Logo" className="init-logo" />
                
                <Typography variant="h5" component="h1" className="init-main-text">
                    {t('initializationPage.mainText')}
                </Typography>
                
                <CircularProgress color="secondary" sx={{ margin: '1.5rem 0' }} />
                
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentTipIndex}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.5 }}
                        className="init-tip-container" // Use a container for consistent height
                    >
                        <Typography variant="body1" className="init-tip-text">
                            {shuffledTips[currentTipIndex]}
                        </Typography>
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default InitializationPage;